import { formatShowTime, parseLooseTime, showNightMinute } from "../../../app/showNight.js";

/**
 * The two derivations that make the run-of-show grid feel like a schedule rather than a form: the auto-fill
 * CASCADE (set one act's time, the rest follow) and the live DAY-OF-SHOW PREVIEW.
 *
 * Both are PURE over the grid's own state — no storage, no API, no React — because both are entirely about
 * arithmetic on times and that arithmetic is where the reference's real behaviour lives.
 */

/** An act's row as the cascade sees it. `autoStart` is EDITOR state and is never persisted. */
export interface CascadeAct {
  slotId: string;
  /** Headliner / co-headliner take the headliner changeover for the gap BEFORE them (D4). */
  isHeadliner: boolean;
  /** The display string the input shows — `9pm`, `8:30 pm`, or empty. */
  setTime: string;
  /** Set length in minutes, as the picker's string value. */
  setLength: string;
  /**
   * True when this act's time was DERIVED by the cascade and may be overwritten again. A time the user typed
   * anchors the chain and is never touched. Legacy strips this on save and so do we — the distinction dies
   * with the editing session, because a derived time and a typed one are the same time once stored.
   */
  autoStart: boolean;
}

/**
 * Flow set times forward down the bill: each act starts when the previous one ends, plus the changeover.
 *
 * **Direction.** Legacy's loop walks BACKWARDS (`for (let i = length - 2; i >= 0; i--)`) because its lineup
 * array is stored headliner-first, so its index `i` is *later* than `i+1`. Our grid is already sorted
 * opener→headliner, so the identical rule reads forwards. Transcribing legacy's loop literally would run the
 * whole thing inside out — the headliner would anchor and the opener would be derived.
 *
 * **The gap is per-act.** Legacy used the support changeover for every gap and never read the headliner one
 * at all — it was a control that did nothing. We ruled to make it real (D4): the headliner changeover applies
 * to the gap before a headliner or co-headliner, the support one everywhere else.
 *
 * An act only derives when the one before it has BOTH a readable time and a length — without a length there
 * is nothing to add, and guessing a default here would fabricate a time the user never gave.
 */
export function applyCascade(
  acts: readonly CascadeAct[],
  changeoverSupportMin: number,
  changeoverHeadlinerMin: number,
): CascadeAct[] {
  const next = acts.map((a) => ({ ...a }));
  for (let i = 1; i < next.length; i++) {
    const cur = next[i];
    const prev = next[i - 1];
    // A time the user typed anchors the chain — the cascade flows around it, never over it.
    if (cur.setTime.trim() !== "" && !cur.autoStart) continue;

    const prevStart = parseLooseTime(prev.setTime);
    const prevLen = Number.parseInt(prev.setLength, 10);
    if (prevStart == null || !Number.isFinite(prevLen) || prevLen <= 0) continue;

    const gap = cur.isHeadliner ? changeoverHeadlinerMin : changeoverSupportMin;
    cur.setTime = formatShowTime(prevStart + prevLen + (Number.isFinite(gap) ? gap : 0));
    cur.autoStart = true;
  }
  return next;
}

/**
 * A typed set time. Clearing the field hands the act BACK to the cascade rather than leaving a hole — an
 * empty input means "you work it out", which is what it looks like it means.
 *
 * **A DELIBERATE DIVERGENCE FROM LEGACY, in the D4 family.** Legacy calls `markDownstreamAuto(next, i)`
 * before every cascade, which sets `_autoStart = true` on every LATER act unconditionally — typed ones
 * included — and then overwrites them. So legacy's anchor rule (`hasManual`) only survives until you touch
 * anything upstream, which makes the feature nearly inert: type the headliner's slot, nudge the opener, and
 * the headliner's time is silently replaced.
 *
 * We keep anchors. Nothing needs re-opening: {@link applyCascade} already recomputes any act still marked
 * auto, so a derived chain re-flows on its own while a typed time holds. Silently discarding a value the
 * user explicitly entered is the defect class this whole overhaul exists to remove.
 */
export function setTimeEdited(
  acts: readonly CascadeAct[],
  index: number,
  value: string,
  changeoverSupportMin: number,
  changeoverHeadlinerMin: number,
): CascadeAct[] {
  const next = acts.map((a, i) => (i === index ? { ...a, setTime: value, autoStart: value.trim() === "" } : a));
  return applyCascade(next, changeoverSupportMin, changeoverHeadlinerMin);
}

/** A changed set length moves the derived acts after it, since the next start hangs off this end. */
export function setLengthEdited(
  acts: readonly CascadeAct[],
  index: number,
  value: string,
  changeoverSupportMin: number,
  changeoverHeadlinerMin: number,
): CascadeAct[] {
  const next = acts.map((a, i) => (i === index ? { ...a, setLength: value } : a));
  return applyCascade(next, changeoverSupportMin, changeoverHeadlinerMin);
}

// ---- the live day-of-show preview ------------------------------------------------------------------------

export interface PreviewAct {
  name: string;
  loadIn: string;
  soundcheck: string;
  scMin: string;
  setTime: string;
  setLength: string;
  loadOut: string;
}

export interface PreviewRow {
  time: string;
  label: string;
  /** Show-night axis position; `.1` marks an END so it sorts after a start at the same minute. */
  sort: number;
}

/**
 * The running order, derived live from what is actually in the grid.
 *
 * **Two rules carry it.** *No fabricated times* — a row is omitted whenever its source field is blank or
 * unreadable, so the preview can be trusted as a picture of what is entered rather than what was assumed. And
 * an "ends" row sorts `+0.1` past its start, so a set that ends at 8:45 reads after a set that starts at 8:45
 * instead of racing it.
 *
 * **Sorted on the show-night axis**, not raw clock minutes — the same helper the server-side read order and
 * the reminder sweep use. Legacy sorted raw and so could not represent a night that crosses midnight: a 00:30
 * headliner sorted to the very top, above the 4pm load-in. Ours is the surface where that is most visible.
 *
 * **Per-act load-in and load-out rows are ours, not legacy's.** Legacy's preview showed only a single
 * "Headliner load-in" because its per-act load fields were write-only — captured and read by nothing. D3
 * wired them, and a wired field that appears nowhere is the same dead-field defect in a new coat.
 */
export function buildDosPreview(input: {
  acts: readonly PreviewAct[];
  doors: string;
  showEnd: string;
}): PreviewRow[] {
  const rows: PreviewRow[] = [];

  const push = (raw: string, label: string, isEnd = false) => {
    const m = parseLooseTime(raw);
    if (m == null) return;
    rows.push({ time: formatShowTime(m), label, sort: showNightMinute(m) + (isEnd ? 0.1 : 0) });
  };
  const pushSpan = (raw: string, minutes: string, label: string, endLabel: string) => {
    const start = parseLooseTime(raw);
    if (start == null) return;
    push(raw, label);
    const len = Number.parseInt(minutes, 10);
    // Only when a length is actually set — an end we invented is a time nobody entered.
    if (Number.isFinite(len) && len > 0) {
      const end = start + len;
      rows.push({ time: formatShowTime(end), label: endLabel, sort: showNightMinute(end) + 0.1 });
    }
  };

  push(input.doors, "Doors");
  for (const a of input.acts) {
    const who = a.name.trim() || "Act";
    push(a.loadIn, `${who}, load in`);
    pushSpan(a.soundcheck, a.scMin, `${who}, soundcheck`, `${who}, soundcheck ends`);
    pushSpan(a.setTime, a.setLength, `${who}, set`, `${who}, set ends`);
    push(a.loadOut, `${who}, load out`);
  }
  push(input.showEnd, "Show end");

  return rows.sort((a, b) => a.sort - b.sort);
}
